/**
 * AdminEntryEditor — единый редактор записи в режиме «внести за сотрудника»
 * (задача 77 §3.1/§4.1).
 *
 * Тонкая обёртка над `EntryEditorCore` (`surface="desktop-dock"`) + admin-адаптер
 * (`useAdminEntryAdapter`). Открывается отдельным правым `<Sheet>`-слоем поверх
 * дневной оболочки (док мастера `EntryDock` или страница backfill) — ядро владеет
 * своим контейнером. Финал (sign/approve) принадлежит оболочке, не редактору;
 * адаптер только пишет запись и закрывает слой по успеху.
 *
 * Общий для обеих admin-оболочек, чтобы состав записи был одинаков —
 * `myWorkTypes` собирает `useAdminEntryWorkTypes`.
 */
import * as React from "react";

import type { TimesheetEntry, WorkType } from "@/api/admin-timesheet";
import { useAdminRecentProjects } from "@/hooks/useAdminTimesheet";

import { EntryEditorCore } from "./EntryEditorCore";
import { useAdminEntryAdapter } from "./adminEntryAdapter";

/** Что открыто в редакторе-слое: новая запись или правка существующей. */
export type AdminEntryTarget = { mode: "create" } | { mode: "edit"; entry: TimesheetEntry };

export interface AdminEntryEditorProps {
  userId: number;
  date: string;
  target: AdminEntryTarget;
  myWorkTypes: readonly WorkType[];
  /** Закрыть слой редактора (× / ESC / после успешной мутации). */
  onClose: () => void;
}

export function AdminEntryEditor({
  userId,
  date,
  target,
  myWorkTypes,
  onClose,
}: AdminEntryEditorProps): React.ReactElement {
  const adapter = useAdminEntryAdapter({
    userId,
    date,
    onClose,
    onSaved: () => onClose(),
    onDeleted: () => onClose(),
  });
  // Чипы-подсказки проектов сотрудника за последний месяц (§4.10) — тот же
  // источник, что у self в PWA. Пустая история → Step2Project прячет чипы.
  const recentProjects = useAdminRecentProjects(userId);
  return (
    <EntryEditorCore
      mode={target.mode}
      initialEntry={target.mode === "edit" ? target.entry : undefined}
      recentProjects={recentProjects.data ?? []}
      myWorkTypes={myWorkTypes}
      adapter={adapter}
      onClose={onClose}
      surface="desktop-dock"
    />
  );
}
